import { useApi } from '~/restAPI';
import { Seo } from '~/restAPI/Seo';

import {
  DeeplinkResolveResultOfobject, SeoDataDTO,
} from '~/restAPI/data-contracts';

export function useCatalogSeo(data: Ref<DeeplinkResolveResultOfobject | null | undefined>) {
  const seoData = useState<SeoDataDTO | undefined>(() => undefined);

  const route = useRoute();
  const { createApiInstance } = useApi();

  const seoApi = createApiInstance(Seo);

  const brandFromDeepLink = computed(() => data.value?.catalogFilter?.brand);
  const modelFromDeepLink = computed(() => data.value?.catalogFilter?.model);

  const catalogFilter = computed(() => getCatalogFilter());

  function getCatalogFilter(res?: DeeplinkResolveResultOfobject | null) {
    const filter = { ...(res?.catalogFilter ?? data.value?.catalogFilter) };

    if (brandFromDeepLink.value) {
      delete filter.brand;
    }

    return {
      ...filter,
      ...mappedParams(route.query),
    };
  }

  function getCurrentCategoryId(res?: DeeplinkResolveResultOfobject | null) {
    const result = res ?? data.value;

    if (result?.object && Array.isArray(result?.object)) {
      const items = result!.object as IBreadCrumbsItem[];
      const lastEl = items[items.length - 1];

      return lastEl.id;
    }

    let categoryId: number | undefined;
    const filter = res ? getCatalogFilter(res) : catalogFilter.value;
    const categories = filter.category as number[] | undefined;

    if (categories?.length === 1) {
      categoryId = categories[0];
    } else if (res) {
      categoryId = res.catalogFilter?.category ? Number(res.catalogFilter.category) : undefined;
    } else {
      const parentCategories = data.value?.catalogFilter?.category as number[] | undefined;

      categoryId = parentCategories?.length
        ? Number(parentCategories[0])
        : undefined;
    }

    return categoryId;
  }

  function getCurrentBrandId(res?: DeeplinkResolveResultOfobject | null) {
    if (!res?.brandInPath && !data.value?.brandInPath) return undefined;

    if (brandFromDeepLink.value) {
      const filter = { ...(res?.catalogFilter ?? data.value?.catalogFilter) };
      const brands = filter.brand as number[] | undefined;

      return brands?.length ? brands[0] : undefined;
    }

    const filter = res ? getCatalogFilter(res) : catalogFilter.value;

    const brands = filter.brand as number[] | undefined;

    return brands?.length === 1 ? brands[0] : undefined;
  }

  function getCurrentModelId(res?: DeeplinkResolveResultOfobject | null) {
    if (!res?.modelInPath && !data.value?.modelInPath) return undefined;

    if (modelFromDeepLink.value) {
      const filter = { ...(res?.catalogFilter ?? data.value?.catalogFilter) };

      const models = filter.model as number[] | undefined;

      return models?.length ? models[0] : undefined;
    }

    const filter = res ? getCatalogFilter(res) : catalogFilter.value;

    const models = filter.model as number[] | undefined;

    return models?.length === 1 ? models[0] : undefined;
  }

  async function getSeo(res?: DeeplinkResolveResultOfobject | null) {
    const categoryId = getCurrentCategoryId(res);
    const brandId = getCurrentBrandId(res);
    const modelId = getCurrentModelId(res);

    const { data: dataSeo } = await useAsyncData(async () => {
      if (!categoryId) return Promise.resolve({});

      const params = {
        categoryId,
        brandId,
        modelId,
      };

      const response = await seoApi.getDataUsingGet2(params, { format: 'json' });

      seoData.value = response.data.data;

      return response.data.data;
    });

    return dataSeo.value;
  }

  const seoParams = computed(() => {
    const result = {
      currentCategoryId: getCurrentCategoryId(),
      currentBrandId: getCurrentBrandId(),
    };

    return result;
  });

  watch(seoParams, () => {
    if (!process.server) {
      getSeo();
    }
  });

  return {
    catalogFilter,
    getSeo,
    seoData,
  };
}
